import { useMemo, useRef } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { HERO_COLORS } from "./constants";

import dayUrl      from "@/assets/earth/earth_day.jpg";
import nightUrl    from "@/assets/earth/earth_night.png";
import cloudsUrl   from "@/assets/earth/earth_clouds.png";
import normalUrl   from "@/assets/earth/earth_normal.jpg";
import specularUrl from "@/assets/earth/earth_specular.jpg";

/* ---------- Custom day-night blend shader ---------------------------------
 * - Samples day + night maps
 * - Blends by dot(worldNormal, sunDirection)
 * - Adds a subtle violet rim right on the terminator (NEVER blue)
 * ------------------------------------------------------------------------ */
const EARTH_VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vViewDir;
  void main() {
    vUv = uv;
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vViewDir = normalize(cameraPosition - worldPos.xyz);
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

const EARTH_FRAG = /* glsl */ `
  uniform sampler2D dayTex;
  uniform sampler2D nightTex;
  uniform sampler2D specTex;
  uniform vec3 sunDir;
  uniform vec3 rimColor;
  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vViewDir;
  void main() {
    vec3 day   = texture2D(dayTex,   vUv).rgb;
    vec3 night = texture2D(nightTex, vUv).rgb;
    float spec = texture2D(specTex,  vUv).r;

    float sunDot = dot(normalize(vWorldNormal), normalize(sunDir));
    float dayMix = smoothstep(-0.10, 0.30, sunDot);

    /* Night side: boost city-light emissive so it feels alive. */
    vec3 nightBoost = night * 1.55;

    /* Very slight desaturation of day for cinematic feel. */
    float lum = dot(day, vec3(0.299, 0.587, 0.114));
    vec3 daySat = mix(vec3(lum), day, 0.92);

    vec3 color = mix(nightBoost, daySat, dayMix);

    /* Subtle purple rim on the terminator + silhouette. */
    float rim = 1.0 - clamp(dot(vWorldNormal, vViewDir), 0.0, 1.0);
    float term = 1.0 - abs(sunDot);
    color += rimColor * pow(rim, 2.4) * 0.35;
    color += rimColor * pow(term, 4.0) * 0.22;

    /* Specular highlight on oceans, tinted pink instead of white. */
    float oceanSpec = spec * pow(max(dot(reflect(-normalize(sunDir), vWorldNormal), vViewDir), 0.0), 24.0);
    color += vec3(0.92, 0.55, 0.85) * oceanSpec * 0.35 * dayMix;

    gl_FragColor = vec4(color, 1.0);
  }
`;

export default function EarthMesh({ radius = 1 }) {
  const earthRef  = useRef();
  const cloudsRef = useRef();

  const [dayTex, nightTex, cloudsTex, normalTex, specTex] = useLoader(THREE.TextureLoader, [
    dayUrl, nightUrl, cloudsUrl, normalUrl, specularUrl,
  ]);

  // Ensure high-quality sampling
  useMemo(() => {
    [dayTex, nightTex, cloudsTex, normalTex, specTex].forEach((t) => {
      t.anisotropy = 8;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.colorSpace = THREE.SRGBColorSpace;
    });
  }, [dayTex, nightTex, cloudsTex, normalTex, specTex]);

  const uniforms = useMemo(
    () => ({
      dayTex:   { value: dayTex   },
      nightTex: { value: nightTex },
      specTex:  { value: specTex  },
      sunDir:   { value: new THREE.Vector3(3.0, 1.2, 2.0).normalize() },
      rimColor: { value: new THREE.Color(HERO_COLORS.glow) },
    }),
    [dayTex, nightTex, specTex],
  );

  useFrame((_, delta) => {
    // The earth sphere is rotated by the parent `earthGroupRef` in EarthScene
    // so click-to-focus can move both the earth AND its markers/arcs together.
    // We only spin the cloud shell here so the atmosphere still feels alive
    // while the group is paused (during the 5s focus hold).
    if (cloudsRef.current) cloudsRef.current.rotation.y += delta * 0.028;
  });

  return (
    <group>
      {/* Planet body */}
      <mesh ref={earthRef} castShadow receiveShadow>
        <sphereGeometry args={[radius, 96, 96]} />
        <shaderMaterial
          vertexShader={EARTH_VERT}
          fragmentShader={EARTH_FRAG}
          uniforms={uniforms}
        />
      </mesh>

      {/* Cloud shell */}
      <mesh ref={cloudsRef}>
        <sphereGeometry args={[radius * 1.008, 96, 96]} />
        <meshStandardMaterial
          map={cloudsTex}
          alphaMap={cloudsTex}
          transparent
          depthWrite={false}
          opacity={0.55}
          color={new THREE.Color("#f5e8ff")}
          emissive={new THREE.Color(HERO_COLORS.glow)}
          emissiveIntensity={0.08}
          normalMap={normalTex}
          normalScale={new THREE.Vector2(0.4, 0.4)}
        />
      </mesh>
    </group>
  );
}
